<template>
  <section class="mx-auto max-w-[1180px] px-4 pb-32 pt-4 text-dark lg:py-16">
    <div v-if="loadFailed" class="mb-6 rounded-[32px] bg-light px-8 py-10">
      <LoadError @retry="loadPage" />
    </div>

    <thanks v-if="page === 'thanks'" />

    <div v-else-if="!cartItems.length" class="rounded-[32px] bg-light px-8 py-14 text-center">
      <h2 class="text-2xl font-semibold">Your cart is empty</h2>
      <p class="mt-3 text-dark/70">Add tickets before heading to checkout.</p>
      <a href="/visit-experiences/" class="mt-8 inline-flex rounded-full bg-secondary px-8 py-4 font-semibold text-white">Browse experiences</a>
    </div>

    <div v-else>
      <Checkout
        @setPage="setPage"
        @top="top"
        :visibleMessages="visibleMessages"
      />
    </div>
  </section>
</template>

<script setup>
import "./helpers/form-setup"
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { Checkout, Thanks } from './routes'
import useCheckoutStore, { useCheckoutCartStore } from './store'
import { createZohoChatVisibilityController } from './helpers/zoho-chat'
import { reportLoadError } from '../helpers/network'
import LoadError from './components/LoadError.vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Checkout',
  },
})

const page = ref('book')
const loadFailed = ref(false)
const cartStore = useCheckoutCartStore()
const checkoutStore = useCheckoutStore()
const { cartItems, cartMessages } = storeToRefs(cartStore)
const visibleMessages = ref([])
const mobileFooterHiddenClass = 'checkout-mobile-hide-footer'
const zohoChat = createZohoChatVisibilityController()
let cartExpiryInterval = null

const currentPath = () => window.location.pathname.replace(/\/$/, '')

const preservedQueryParams = () => {
  const currentParams = new URLSearchParams(window.location.search)
  const preservedParams = new URLSearchParams()

  ;['booking_mode', 'booking_smoke', 'booking_key'].forEach((key) => {
    if (currentParams.has(key)) {
      preservedParams.set(key, currentParams.get(key))
    }
  })

  return preservedParams
}

const baseCheckoutPath = () => {
  return currentPath().replace(/\/thanks$/, '') || '/'
}

const thanksPath = () => {
  return `${baseCheckoutPath().replace(/\/$/, '')}/thanks/`
}

const bookingNumberForThanks = () => {
  return cartStore.booking?.internal_id ?? cartStore.bookingNumber ?? cartStore.booking?.orderNumber ?? ''
}

const thanksUrl = () => {
  const bookingNumber = bookingNumberForThanks()
  const token = cartStore.bookingToken ?? ''

  if (!bookingNumber || !token) {
    return ''
  }

  const params = preservedQueryParams()
  params.set('booking', bookingNumber)
  params.set('token', token)

  return `${thanksPath()}?${params.toString()}`
}

const syncPageWithLocation = async () => {
  const onThanksPath = currentPath().endsWith('/thanks')

  page.value = onThanksPath ? 'thanks' : 'book'

  if (!onThanksPath) {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  const bookingNumber = params.get('booking') || bookingNumberForThanks()
  const token = params.get('token') || cartStore.bookingToken

  if (!bookingNumber || !token) {
    return true
  }

  await cartStore.ensureLoaded()

  const bookingMatches =
    String(cartStore.bookingNumber ?? '') === String(bookingNumber) &&
    String(cartStore.bookingToken ?? '') === String(token)

  if (!bookingMatches || !cartStore.booking?.orderNumber) {
    await cartStore.loadBooking(bookingNumber, token)
  }

  return true
}

const pushThanksUrl = () => {
  const nextUrl = thanksUrl()

  if (!nextUrl) {
    return
  }

  const currentUrl = `${window.location.pathname}${window.location.search}`

  if (currentUrl === nextUrl) {
    return
  }

  window.history.pushState({ page: 'thanks' }, '', nextUrl)
}

const pushCheckoutUrl = () => {
  const params = preservedQueryParams()
  const nextUrl = baseCheckoutPath()
  const nextUrlWithQuery = params.toString() ? `${nextUrl}?${params.toString()}` : nextUrl
  const currentUrl = `${window.location.pathname}${window.location.search}`

  if (currentUrl === nextUrlWithQuery) {
    return
  }

  window.history.pushState({ page: 'book' }, '', nextUrlWithQuery)
}

const syncCartMessages = () => {
  if (!cartMessages.value.length) {
    return
  }

  visibleMessages.value = [...visibleMessages.value, ...cartMessages.value]
  cartStore.clearCartMessages()
}

const syncToCheckoutStore = async () => {
  await cartStore.ensureLoaded()
  cartStore.pruneUnavailableCart()
  syncCartMessages()
  cartStore.pruneExpiredCart()

  if (!cartStore.cartItems.length) {
    return
  }

  cartStore.refreshCartReservations()
  checkoutStore.syncCartToItems()
}

const setPage = (nextPage) => {
  page.value = nextPage

  if (nextPage === 'thanks') {
    cartStore.clearCart()
    pushThanksUrl()
    return
  }

  if (nextPage === 'book') {
    pushCheckoutUrl()
  }
}

const top = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const loadPage = async () => {
  loadFailed.value = false

  try {
    const onThanksPage = await syncPageWithLocation()

    if (!onThanksPage) {
      await syncToCheckoutStore()
    }
  } catch (error) {
    loadFailed.value = true
    reportLoadError(error)
  }
}

const handlePopState = loadPage


onMounted(async () => {
  document.body.classList.add(mobileFooterHiddenClass)
  zohoChat.setVisibility(false)
  await loadPage()

  cartExpiryInterval = window.setInterval(() => {
    if (page.value === 'thanks') {
      return
    }

    cartStore.pruneUnavailableCart()
    syncCartMessages()
    cartStore.pruneExpiredCart()
  }, 1000)

  window.addEventListener('popstate', handlePopState)
})

onBeforeUnmount(() => {
  document.body.classList.remove(mobileFooterHiddenClass)
  zohoChat.setVisibility(true)

  if (cartExpiryInterval) {
    window.clearInterval(cartExpiryInterval)
  }

  window.removeEventListener('popstate', handlePopState)
})
</script>
